/**
 * @file MaintenanceBanner.js
 * @version 2.0.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-08-18
 * @modified 2026-08-26
 * @description What the office sees during a mid-day update. Three states:
 *
 *                warning  update coming, live countdown, save what is open
 *                during   update running, read but don't type
 *                stale    update finished, this tab needs a hard refresh
 *
 *              Every state is a strip, and nothing is blocked. The middle state
 *              used to be a blocking overlay backed by a server-side 423, on the
 *              reasoning that letting someone type into a system that will
 *              refuse the save only manufactures work they will lose.
 *
 *              That reasoning was sound and the trade was still wrong. A deploy
 *              is about ten seconds; locking the whole office out of *reading*
 *              for a minute to protect against a few seconds of possible lost
 *              typing costs more than it saves — someone on the phone to a
 *              customer needs to look up a price, and "the system is down" is a
 *              worse answer than a half-loaded page that fixes itself on the
 *              refresh that follows anyway.
 *
 *              So it advises rather than prevents, and it advises everyone the
 *              same: admins were never blocked and had their own wording, which
 *              is now the only wording.
 */

import React, { useEffect, useState } from 'react';
import './MaintenanceBanner.css';

// Cache-busting reload. A plain reload can be served the old bundle from the
// disk cache; adding a changing query string guarantees a fresh index.html.
function hardReload() {
  const url = new URL(window.location.href);
  url.searchParams.set('_v', Date.now().toString(36));
  window.location.replace(url.toString());
}

function fmt(secs) {
  if (secs === null || secs === undefined) return '';
  if (secs >= 60) {
    const m = Math.floor(secs / 60);
    const s = secs % 60;
    return `${m}m ${String(s).padStart(2, '0')}s`;
  }
  return `${secs}s`;
}

export default function MaintenanceBanner({ phase, lock, secondsUntilLock }) {
  const [autoIn, setAutoIn] = useState(null);

  // Once the update is done, reload on a short delay rather than instantly, so
  // the message is readable and a click isn't stolen mid-press.
  useEffect(() => {
    if (phase !== 'stale') { setAutoIn(null); return undefined; }
    setAutoIn(10);
    const id = setInterval(() => {
      setAutoIn(v => {
        if (v === null) return null;
        if (v <= 1) { clearInterval(id); hardReload(); return 0; }
        return v - 1;
      });
    }, 1000);
    return () => clearInterval(id);
  }, [phase]);

  const open = !!phase && phase !== 'idle';
  const message = lock?.message;

  /**
   * The slot is always in the document; only its height changes.
   *
   * A fixed strip laid itself over the top of the app and hid whatever was
   * underneath — usually the page heading. Animating a grid row from 0fr to 1fr
   * is the one way to transition to an automatic height, so the whole app slides
   * down to make room and slides back when it is over.
   */
  return (
    <div className={`maint-slot${open ? ' open' : ''}`} aria-hidden={!open}>
      <div className="maint-slot-inner">
        {phase === 'warning' && (
          <div className="maint-strip maint-warning" role="status" aria-live="polite">
            <span className="material-symbols-rounded">schedule</span>
            <span className="maint-text">
              <strong>Update in {fmt(secondsUntilLock)}.</strong>{' '}
              {message || 'Please save anything you have open.'}
            </span>
          </div>
        )}

        {phase === 'locked' && (
          <div className="maint-strip maint-during" role="status" aria-live="polite">
            <span className="material-symbols-rounded">deployed_code_update</span>
            <span className="maint-text">
              <strong>Update in progress.</strong>{' '}
              {message || 'Please save anything open and avoid entering new information for the next minute. Looking things up is fine — the page refreshes itself when the update finishes.'}
            </span>
          </div>
        )}

        {phase === 'stale' && (
          <div className="maint-strip maint-done" role="status" aria-live="polite">
            <span className="material-symbols-rounded">check_circle</span>
            <span className="maint-text">
              <strong>Update finished.</strong> This page is refreshing
              {autoIn !== null ? ` in ${autoIn}s` : ''} to pick up the new version.
            </span>
            <button type="button" className="maint-btn" onClick={hardReload}>Refresh now</button>
          </div>
        )}
      </div>
    </div>
  );
}
